import { Controller } from "@hotwired/stimulus";

// 3-step wizard for creating a JEstion form, used both inside the new-form
// modal on the forms index AND on the standalone /forms/new page (via
// `_form_wizard.html.erb`).
//
//   0. Source     — vierge OR template (renders a picker grid when "template"
//                   is selected; pickTemplate stamps a hidden input).
//   1. Identité   — title (required) + description (optional, recap shows it).
//   2. Paramètres — anonymous toggle + final recap.
//
// Form posts to /juniors/:junior_id/jestionform/forms which redirects to the
// new form's edit page on success.
export default class extends Controller {
  static targets = [
    "form",
    "step", "progressStep", "progressCurrent",
    "prevBtn", "nextBtn", "submitBtn", "error",
    // Step 0
    "modeCard", "templatePicker",
    // Step 1
    "titleField", "descField",
    // Step 2
    "anonField",
    // Recap
    "recap", "recapSource", "recapTitle", "recapAnon",
  ];

  static values = {
    current: { type: Number, default: 0 },
    mode:    { type: String, default: "blank" },
  };

  connect() {
    this._render();
  }

  // ── Navigation ──────────────────────────────────────────────────────

  next(event) {
    event.preventDefault();
    if (!this._validateCurrent()) return;
    if (this.currentValue >= 2) return;
    this.currentValue += 1;
    this._render();
    if (this.currentValue === 2) this._refreshRecap();
  }

  prev(event) {
    event.preventDefault();
    if (this.currentValue <= 0) return;
    this.currentValue -= 1;
    this._render();
  }

  // ── Step 0 — source choice ──────────────────────────────────────────

  switchMode(event) {
    const mode = event.currentTarget.value;
    this.modeValue = mode;
    this.modeCardTargets.forEach((card) => {
      card.classList.toggle("csm_mode-card--active", card.dataset.mode === mode);
    });
    if (this.hasTemplatePickerTarget) {
      this.templatePickerTarget.hidden = mode !== "template";
    }
    // If user switches back to "blank", clear any previously picked template.
    if (mode === "blank") {
      this.element.querySelectorAll('input[name="template_id"]:checked').forEach((r) => { r.checked = false; });
    }
    this._hideError();
  }

  pickTemplate() {
    // Highlight the picked card visually (no-op for state — the radio handles it).
    this.element.querySelectorAll(".jf-template-card").forEach((card) => {
      const radio = card.querySelector('input[name="template_id"]');
      card.classList.toggle("jf-template-card--active", !!radio?.checked);
    });
    this._hideError();
  }

  // ── Step 1+2 input handlers ─────────────────────────────────────────

  updateRecap() {
    if (this.currentValue === 2) this._refreshRecap();
  }

  // ── Validation ──────────────────────────────────────────────────────

  _validateCurrent() {
    this._hideError();
    if (this.currentValue === 0) {
      if (this.modeValue === "template") {
        const picked = this.element.querySelector('input[name="template_id"]:checked');
        if (!picked) {
          this._showError("Choisis un template, ou bascule en formulaire vierge.");
          return false;
        }
      }
      return true;
    }
    if (this.currentValue === 1) {
      const title = (this.titleFieldTarget.value || "").trim();
      if (!title) {
        this._showError("Le titre est obligatoire.");
        this.titleFieldTarget.focus();
        return false;
      }
      return true;
    }
    return true;
  }

  // ── Rendering ───────────────────────────────────────────────────────

  _render() {
    this.stepTargets.forEach((s) => {
      const idx = Number(s.dataset.step);
      const active = idx === this.currentValue;
      s.classList.toggle("esm_wizard__step--active", active);
      s.hidden = !active;
    });

    this.progressStepTargets.forEach((p) => {
      const i = Number(p.dataset.step);
      p.classList.toggle("esm_wizard__progress-step--active", i === this.currentValue);
      p.classList.toggle("esm_wizard__progress-step--done",   i < this.currentValue);
    });
    if (this.hasProgressCurrentTarget) {
      this.progressCurrentTarget.textContent = this.currentValue + 1;
    }

    if (this.hasPrevBtnTarget) this.prevBtnTarget.hidden = this.currentValue === 0;
    const isLast = this.currentValue === 2;
    if (this.hasNextBtnTarget)   this.nextBtnTarget.hidden   = isLast;
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.hidden = !isLast;
  }

  _refreshRecap() {
    if (!this.hasRecapTarget) return;

    if (this.hasRecapSourceTarget) {
      if (this.modeValue === "template") {
        const picked = this.element.querySelector('input[name="template_id"]:checked');
        const card = picked ? picked.closest(".jf-template-card") : null;
        const name = card?.querySelector(".jf-template-card__title")?.textContent?.trim() || "Template";
        this.recapSourceTarget.textContent = `Template — ${name}`;
      } else {
        this.recapSourceTarget.textContent = "Formulaire vierge";
      }
    }

    if (this.hasRecapTitleTarget) {
      this.recapTitleTarget.textContent = (this.titleFieldTarget?.value || "").trim() || "—";
    }

    if (this.hasRecapAnonTarget) {
      this.recapAnonTarget.textContent = this.anonFieldTarget?.checked ? "Oui" : "Non";
    }
  }

  // ── Helpers ─────────────────────────────────────────────────────────

  _showError(msg) {
    if (!this.hasErrorTarget) return;
    this.errorTarget.textContent = msg;
    this.errorTarget.hidden = false;
  }

  _hideError() {
    if (!this.hasErrorTarget) return;
    this.errorTarget.hidden = true;
    this.errorTarget.textContent = "";
  }
}
